// frontend/src/Customers/Customers.jsx
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { assignCustomer, getCustomers, deleteCustomer } from '../api/customerApi';
import { getUsers } from '../api/authApi';
import styles from './Customers.module.css';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';

const translations = {
  en: {
    manageCustomers: 'Manage Customers',
    addNewCustomer: '+ Add New Customer',
    noCustomers: 'No customers found. Add a new customer to get started.',
    noFilteredCustomers: 'No customers match the current filters.',
    filters: 'Filters',
    searchPlaceholder: 'Search name, organization, or phone...',
    customerIdPlaceholder: 'Customer ID',
    locationPlaceholder: 'Governorate / area',
    clearFilters: 'Clear Filters',
    name: 'Full Name',
    organizationName: 'Organization',
    phoneNumber: 'Phone Number',
    customerId: 'Customer ID',
    assignedTo: 'Assigned To',
    salesUser: 'Sales User',
    allSalesUsers: 'All sales users',
    unassigned: 'Unassigned',
    actions: 'Actions',
    viewProfile: 'View Profile',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this customer?',
    yes: 'Yes',
    no: 'No',
    failedFetch: 'Failed to fetch customers. Please try again.',
    failedDelete: 'Failed to delete customer. Please try again.',
    failedAssign: 'Failed to assign customer. Please try again.',
    deleteSuccess: 'Customer deleted successfully.',
    assignSuccess: 'Customer assignment updated successfully.',
    errorTitle: 'Error',
    successTitle: 'Success',
    close: 'Close',
    loading: 'Loading...',
    homepage: 'Homepage',
  },
  ar: {
    manageCustomers: 'إدارة العملاء',
    addNewCustomer: '+ أضف عميل جديد',
    noCustomers: 'لم يتم العثور على عملاء. أضف عميلًا جديدًا للبدء.',
    noFilteredCustomers: 'لا يوجد عملاء مطابقون للفلاتر الحالية.',
    filters: 'الفلاتر',
    searchPlaceholder: 'ابحث بالاسم أو المؤسسة أو الهاتف...',
    customerIdPlaceholder: 'رقم العميل',
    locationPlaceholder: 'المحافظة / المنطقة',
    clearFilters: 'مسح الفلاتر',
    name: 'الاسم الكامل',
    organizationName: 'المؤسسة',
    phoneNumber: 'رقم الهاتف',
    customerId: 'رقم العميل',
    assignedTo: 'مسؤول المبيعات',
    salesUser: 'مندوب المبيعات',
    allSalesUsers: 'كل مندوبي المبيعات',
    unassigned: 'غير معين',
    actions: 'الإجراءات',
    viewProfile: 'عرض الملف الشخصي',
    delete: 'حذف',
    confirmDelete: 'هل أنت متأكد أنك تريد حذف هذا العميل؟',
    yes: 'نعم',
    no: 'لا',
    failedFetch: 'فشل في جلب العملاء. حاول مرة أخرى.',
    failedDelete: 'فشل في حذف العميل. حاول مرة أخرى.',
    failedAssign: 'فشل في تعيين العميل. حاول مرة أخرى.',
    deleteSuccess: 'تم حذف العميل بنجاح.',
    assignSuccess: 'تم تحديث تعيين العميل بنجاح.',
    errorTitle: 'خطأ',
    successTitle: 'نجاح',
    close: 'إغلاق',
    loading: 'جارٍ التحميل...',
    homepage: 'الصفحة الرئيسية',
  },
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [language, setLanguage] = usePersistentLanguage(); // Default language is English
  const [loading, setLoading] = useState(false); // Loading state
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [salesUsers, setSalesUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    customerID: '',
    salesUserId: 'all',
    location: '',
  });
  const navigate = useNavigate();

  const t = (key) => translations[language][key];

  const fetchCustomers = async () => {
    setLoading(true); // Start loading
    try {
      const response = await getCustomers();
      setCustomers(response.data);
      setLoading(false); // Stop loading after successful fetch
    } catch (err) {
      setError(err.response?.data?.message || t('failedFetch'));
      setLoading(false); // Stop loading even if there's an error
    }
  };
  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            userType: payload.userType,
            requesterUsername: payload.username,
          };
        } catch (error) {
          console.error('Error parsing token payload:', error);
          return { userType: null, requesterUsername: null };
        }
      }
      return { userType: null, requesterUsername: null };
    }, [token]);
    
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line
  }, [language]); // Re-fetch when language changes

  const canManageAssignments = ['admin', 'manager', 'sales-manager'].includes(
    userInfo.userType
  );
  const canDeleteCustomers = userInfo.userType === 'admin' || userInfo.userType === 'manager';
  const canAddCustomers =
    userInfo.userType === 'admin' ||
    userInfo.userType === 'manager' ||
    userInfo.userType === 'sales-manager' ||
    userInfo.userType === 'sales';

  useEffect(() => {
    if (!canManageAssignments) {
      return;
    }

    const fetchSalesUsers = async () => {
      try {
        const response = await getUsers({ roles: 'sales' });
        const activeSalesUsers = (response.data || []).filter(
          (user) => user.userType === 'sales' && user.isActive !== false
        );
        setSalesUsers(activeSalesUsers);
      } catch (err) {
        console.error('Error fetching sales users:', err);
      }
    };

    fetchSalesUsers();
  }, [canManageAssignments]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      customerID: '',
      salesUserId: 'all',
      location: '',
    });
  };

  const getAssignedSalesUserId = useCallback((customer) => {
    if (!customer?.salesuser) return '';
    return customer.salesuser._id || customer.salesuser;
  }, []);

  const getAssignedSalesUserName = (customer) => {
    if (!customer?.salesuser) return t('unassigned');
    return customer.salesuser.username || t('unassigned');
  };

  const filteredCustomers = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const customerID = filters.customerID.trim().toLowerCase();
    const location = filters.location.trim().toLowerCase();

    return customers.filter((customer) => {
      const searchable = [
        customer.fullName,
        customer.name,
        customer.organizationName,
        customer.phoneNumber,
        customer.customerID,
        customer.contactPerson,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const locationText = [customer.governorate, customer.mainArea, customer.subArea]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !search || searchable.includes(search);
      const matchesCustomerID =
        !customerID || String(customer.customerID || '').toLowerCase().includes(customerID);
      const matchesLocation = !location || locationText.includes(location);
      const matchesSalesUser =
        filters.salesUserId === 'all' ||
        getAssignedSalesUserId(customer) === filters.salesUserId;

      return matchesSearch && matchesCustomerID && matchesLocation && matchesSalesUser;
    });
  }, [customers, filters, getAssignedSalesUserId]);

  const handleDelete = async () => {
    if (!canDeleteCustomers) {
      closeDeleteModal();
      return;
    }
    setLoading(true); // Start loading
    try {
      await deleteCustomer(selectedCustomerId);
      setCustomers(customers.filter((c) => c._id !== selectedCustomerId));
      setIsModalOpen(false);
      setSuccessMessage(t('deleteSuccess'));
      setShowSuccessModal(true);
      setLoading(false); // Stop loading after successful deletion
    } catch (err) {
      setError(err.response?.data?.message || t('failedDelete'));
      setIsModalOpen(false);
      setLoading(false); // Stop loading even if there's an error
    }
  };

  const openDeleteModal = (id) => {
    if (!canDeleteCustomers) return;
    setSelectedCustomerId(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setSelectedCustomerId(null);
  };

  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === 'en' ? 'ar' : 'en'));
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage(null);
  };

  const handleAssignmentChange = async (customerId, salesUserId) => {
    if (!canManageAssignments || !salesUserId) {
      return;
    }

    setLoading(true);
    try {
      const response = await assignCustomer(customerId, salesUserId);
      setCustomers((prev) =>
        prev.map((customer) =>
          customer._id === customerId ? response.data : customer
        )
      );
      setSuccessMessage(t('assignSuccess'));
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.message || t('failedAssign'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout
      title={t('manageCustomers')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.customersContainer}
      contentProps={{ 'aria-busy': loading }}
    >
      {/* Loading Indicator */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>{t('loading')}</p>
        </div>
      )}

      <main className={styles.mainContent}>
        <div className={styles.topBar}>
          <div>
            <h2 className={styles.sectionTitle}>{t('filters')}</h2>
          </div>
          {canAddCustomers && (
            <button
              className={styles.addCustomerButton}
              onClick={() => navigate('/customers/new')}
              disabled={loading}
            >
              {t('addNewCustomer')}
            </button>
          )}
        </div>

        <section className={styles.filterPanel}>
          <input
            type="search"
            className={styles.filterInput}
            placeholder={t('searchPlaceholder')}
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
          />
          <input
            type="search"
            className={styles.filterInput}
            placeholder={t('customerIdPlaceholder')}
            value={filters.customerID}
            onChange={(event) => updateFilter('customerID', event.target.value)}
          />
          <input
            type="search"
            className={styles.filterInput}
            placeholder={t('locationPlaceholder')}
            value={filters.location}
            onChange={(event) => updateFilter('location', event.target.value)}
          />
          {canManageAssignments && (
            <select
              className={styles.filterInput}
              value={filters.salesUserId}
              onChange={(event) => updateFilter('salesUserId', event.target.value)}
            >
              <option value="all">{t('allSalesUsers')}</option>
              {salesUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className={styles.clearFiltersButton}
            onClick={resetFilters}
          >
            {t('clearFilters')}
          </button>
        </section>

        {error && <p className={styles.errorMessage}>{error}</p>}

        <div className={styles.customerList}>
          {customers.length === 0 ? (
            <p>{t('noCustomers')}</p>
          ) : filteredCustomers.length === 0 ? (
            <p className={styles.emptyState}>{t('noFilteredCustomers')}</p>
          ) : (
            <table className={styles.customerTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('name')}</th>
                  <th>{t('organizationName')}</th>
                  <th>{t('phoneNumber')}</th>
                  <th>{t('customerId')}</th>
                  <th>{t('assignedTo')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, index) => (
                  <tr key={customer._id}>
                    <td>{index + 1}</td>
                    <td>{customer.fullName || customer.name}</td>
                    <td>{customer.organizationName || '-'}</td>
                    <td>{customer.phoneNumber || '-'}</td>
                    <td>{customer.customerID}</td>
                    <td>
                      {canManageAssignments ? (
                        <select
                          className={styles.assignmentSelect}
                          value={getAssignedSalesUserId(customer)}
                          onChange={(event) =>
                            handleAssignmentChange(customer._id, event.target.value)
                          }
                          disabled={loading}
                        >
                          <option value="" disabled>
                            {t('unassigned')}
                          </option>
                          {salesUsers.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user.username}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getAssignedSalesUserName(customer)
                      )}
                    </td>
                    <td className={styles.actionCell}>
                      <button
                        className={styles.viewProfileButton}
                        onClick={() => navigate(`/customers/${customer._id}`)}
                      >
                        {t('viewProfile')}
                      </button>
                      {canDeleteCustomers && (
                        <button
                          className={styles.deleteCustomerButton}
                          onClick={() => openDeleteModal(customer._id)}
                          disabled={loading}
                        >
                          {t('delete')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {isModalOpen && canDeleteCustomers && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p>{t('confirmDelete')}</p>
            <div className={styles.modalActions}>
              <button
                className={`${styles.modalButton} ${styles.modalButtonDanger}`}
                onClick={handleDelete}
                disabled={loading}
              >
                {t('yes')}
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                onClick={closeDeleteModal}
                disabled={loading}
              >
                {t('no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p>{successMessage}</p>
            <button
              className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
              onClick={closeSuccessModal}
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default Customers;
